/**
 * Razorpay Payment Service
 * Frontend service layer for handling Razorpay payment operations
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '../firebase';
import type {
  RazorpayCheckoutOptions,
  RazorpaySuccessResponse,
  PaymentVerificationResponse,
} from '../types/razorpay.types';

const functions = getFunctions(firebaseApp);

/**
 * Create a new Razorpay order
 */
export const createRazorpayOrder = async (
  amount: number,
  planType: string,
  planDuration: string,
  email?: string,
  notes?: { [key: string]: string }
): Promise<{
  success: boolean;
  orderId: string;
  amount: number;
  amountInRupees: number;
  currency: string;
  receipt: string;
}> => {
  try {
    const createOrder = httpsCallable(functions, 'createRazorpayOrder');

    const result = await createOrder({
      amount,
      currency: 'INR',
      planType,
      planDuration,
      email: email || null,
      notes: notes || {},
    });

    const data = result.data as any;

    if (!data.success) {
      throw new Error('Failed to create order');
    }

    return {
      success: data.success,
      orderId: data.orderId,
      amount: data.amount,
      amountInRupees: data.amountInRupees,
      currency: data.currency,
      receipt: data.receipt,
    };
  } catch (error: any) {
    throw new Error(error.message || 'Failed to create order');
  }
};

/**
 * Verify Razorpay payment signature
 */
export const verifyRazorpayPayment = async (
  orderId: string,
  paymentId: string,
  signature: string
): Promise<PaymentVerificationResponse> => {
  try {
    const verifyPayment = httpsCallable(functions, 'verifyRazorpayPayment');

    const result = await verifyPayment({
      orderId,
      paymentId,
      signature,
    });

    const data = result.data as PaymentVerificationResponse;

    if (!data.success) {
      throw new Error(data.message || 'Payment verification failed');
    }

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to verify payment');
  }
};

/**
 * Initialize Razorpay checkout
 */
export const initializeRazorpayCheckout = (
  options: RazorpayCheckoutOptions
): void => {
  // Check if Razorpay script is loaded
  if (!(window as any).Razorpay) {
    throw new Error('Razorpay SDK not loaded. Please include the Razorpay script.');
  }

  const razorpay = new (window as any).Razorpay(options);
  razorpay.open();
};

/**
 * Load Razorpay script dynamically
 */
export const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if script is already loaded
    if ((window as any).Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };

    document.body.appendChild(script);
  });
};

/**
 * Handle Razorpay payment flow
 * Complete flow: create order → open checkout → verify payment
 */
export const handleRazorpayPayment = async (
  amount: number,
  planType: string,
  planDuration: string,
  userDetails: {
    name: string;
    email: string;
    contact?: string;
  },
  notes: { [key: string]: string } = {},
  onSuccess: (response: RazorpaySuccessResponse) => void,
  onFailure: (error: any) => void
): Promise<void> => {
  try {
    // Step 1: Load Razorpay script
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      throw new Error('Failed to load Razorpay payment gateway');
    }

    // Step 2: Create order (pass email for signup flow)
    // Merge user details with custom notes (e.g., selectedPlan for HR)
    const orderNotes = {
      userName: userDetails.name,
      userEmail: userDetails.email,
      ...notes, // Include custom notes like selectedPlan
    };

    const order = await createRazorpayOrder(
      amount,
      planType,
      planDuration,
      userDetails.email, // Email parameter for unauthenticated signup
      orderNotes
    );


    // Step 3: Get Razorpay Key ID from environment
    const razorpayKeyId = import.meta.env.VITE_RAZORPAY_KEY_ID;

    if (!razorpayKeyId) {
      throw new Error('Razorpay Key ID not configured. Please set VITE_RAZORPAY_KEY_ID in .env');
    }

    // Step 4: Initialize Razorpay checkout
    const options: RazorpayCheckoutOptions = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      name: 'VetriaAI',
      description: `${planType} - ${planDuration}`,
      // image: '/logo.png', // Optional - comment out if logo doesn't exist
      order_id: order.orderId,
      handler: async (response: RazorpaySuccessResponse) => {
        try {

          // Step 5: Verify payment on backend
          const verification = await verifyRazorpayPayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );

          onSuccess(response);
        } catch (error: any) {
          onFailure(error);
        }
      },
      prefill: {
        name: userDetails.name,
        email: userDetails.email,
        contact: userDetails.contact || '',
      },
      notes: {
        planType,
        planDuration,
        ...notes, // Include custom notes like selectedPlan
      },
      theme: {
        color: '#3b82f6', // Tailwind blue-500
      },
      modal: {
        ondismiss: () => {
          onFailure(new Error('Payment cancelled by user'));
        },
      },
    };

    initializeRazorpayCheckout(options);
  } catch (error: any) {
    onFailure(error);
  }
};

/**
 * Get plan details by type and duration
 */
export const getPlanDetails = (planType: string, planDuration?: string) => {
  const plans = {
    JOBSEEKER: {
      MONTHLY: {
        name: 'Premium Monthly',
        amount: 199,
        currency: 'INR',
        duration: 'monthly',
        features: [
          'Unlimited personalized courses',
          'Priority support',
          'Advanced analytics',
          'Certificate of completion',
        ],
      },
    },
    HR: {
      CORE_1M: {
        name: 'Core - 1 Month',
        amount: 3999,
        currency: 'INR',
        duration: '1 month',
        features: [
          'Unlimited test creation',
          'Unlimited candidate access',
          'AI-powered interviews',
          'Detailed analytics',
          'Priority support',
        ],
      },
      CORE_3M: {
        name: 'Core - 3 Months',
        amount: 11997,
        currency: 'INR',
        duration: '3 months',
        savings: 1,
        features: [
          'All Core features',
          'Save ₹1',
          'Quarterly billing',
        ],
      },
      CORE_6M: {
        name: 'Core - 6 Months',
        amount: 23994,
        currency: 'INR',
        duration: '6 months',
        savings: 2,
        features: [
          'All Core features',
          'Save ₹2',
          'Half-yearly billing',
        ],
      },
      CORE_12M: {
        name: 'Core - 12 Months',
        amount: 47998,
        currency: 'INR',
        duration: '12 months',
        savings: 2,
        features: [
          'All Core features',
          'Save ₹2',
          'Best value - Annual billing',
        ],
      },
    },
  };

  if (planType === 'JOBSEEKER') {
    return plans.JOBSEEKER.MONTHLY;
  }

  if (planType === 'HR') {
    switch (planDuration) {
      case '1 month':
        return plans.HR.CORE_1M;
      case '3 months':
        return plans.HR.CORE_3M;
      case '6 months':
        return plans.HR.CORE_6M;
      case '12 months':
        return plans.HR.CORE_12M;
      default:
        return plans.HR.CORE_1M;
    }
  }

  throw new Error('Invalid plan type');
};
